import { useCallback, useMemo } from "react";
import { usePartners, usePrices } from "@stockline/web";

/**
 * Price lookup maps for O(1) access.
 *
 * Type kept here for backwards-compatibility — the recap-only adapter
 * (`useRecapPricesOffline`) and the legacy mobile page both consume this
 * shape, and `useEffectivePriceLookup` below resolves prices against it.
 */
export interface PriceLookupMaps {
  // Map: partnerId -> productId -> price
  partnerPrices: Map<string, Map<string, number>>;
  // Map: groupId -> productId -> price
  groupPrices: Map<string, Map<string, number>>;
  // Map: productId -> price (default prices)
  productPrices: Map<string, number>;
  // Map: partnerId -> groupId (for partner group lookup)
  partnerGroups: Map<string, string>;
  // User-scoped maps
  userPartnerPrices: Map<string, Map<string, number>>;
  userGroupPrices: Map<string, Map<string, number>>;
}

/**
 * Global pricing cache — Dexie-backed via `@stockline/web`.
 *
 * The legacy TanStack Query implementation polled 4 separate endpoints
 * (`partner-prices`, `group-prices`, plus user-scoped versions) every 45
 * minutes. SyncEngine now keeps the `prices` Dexie table fresh through
 * SSE; this hook just projects the same `PriceLookupMaps` shape the
 * legacy implementation exposed so call sites keep working unchanged.
 *
 * Per-partner rows were dropped from the domain model when prices moved
 * to the offline-first system — the new `Price` table is keyed on
 * `clientGroupId` only. The `partnerPrices` / `userPartnerPrices` slots
 * are therefore always empty; resolution falls through to client-group
 * prices via `partnerGroups`, which is the intended new behavior. Same
 * trade-off `useRecapPricesOffline` makes.
 *
 * `productPrices` is intentionally left empty — call sites overlay product
 * default prices onto the map themselves (see `useRecapData`,
 * `useFocusViewData`, mobile recap). That kept the legacy contract too.
 */
export function useGlobalPrices(options?: {
  enabled?: boolean;
  userId?: string;
}) {
  const enabled = options?.enabled ?? true;
  const userId = options?.userId;

  const allPrices = usePrices();
  const partners = usePartners();

  const priceMaps = useMemo<PriceLookupMaps>(() => {
    const partnerPrices = new Map<string, Map<string, number>>();
    const groupPrices = new Map<string, Map<string, number>>();
    const productPrices = new Map<string, number>();
    const partnerGroups = new Map<string, string>();
    const userPartnerPrices = new Map<string, Map<string, number>>();
    const userGroupPrices = new Map<string, Map<string, number>>();

    if (!enabled) {
      return {
        partnerPrices,
        groupPrices,
        productPrices,
        partnerGroups,
        userPartnerPrices,
        userGroupPrices,
      };
    }

    for (const row of allPrices) {
      if (row.userId === null) {
        const target = groupPrices.get(row.clientGroupId) ?? new Map();
        target.set(row.productId, row.price);
        groupPrices.set(row.clientGroupId, target);
      } else if (userId && row.userId === userId) {
        const target = userGroupPrices.get(row.clientGroupId) ?? new Map();
        target.set(row.productId, row.price);
        userGroupPrices.set(row.clientGroupId, target);
      }
    }

    // Partner → primary client group. A partner may sit in several groups;
    // only one typically carries priced products — pick the first id that
    // appears in groupPrices or userGroupPrices, otherwise fall back to [0].
    for (const partner of partners) {
      const ids = partner.clientGroupIds;
      if (!ids?.length) continue;
      const priced =
        ids.find((id) => groupPrices.has(id) || userGroupPrices.has(id)) ??
        ids[0];
      partnerGroups.set(partner.id, priced);
    }

    return {
      partnerPrices,
      groupPrices,
      productPrices,
      partnerGroups,
      userPartnerPrices,
      userGroupPrices,
    };
  }, [allPrices, partners, userId, enabled]);

  return {
    isLoading: false,
    isError: false,
    error: null as Error | null,
    priceMaps,
  } as const;
}

/**
 * Source bucket for a resolved effective price.
 *
 * Collapsed view of the 5-tier hierarchy: the recap UI only needs to
 * tell "negotiated" (any of the partner/group/user tiers) apart from
 * "product default" — so callers render TARIF vs LISTE chips without
 * caring whether the price came from a user-scoped or company-scoped
 * map. Keep the resolver returning the granular source so future
 * surfaces (audit popovers, etc.) can still distinguish.
 */
export type PriceSource =
  | "user-partner"
  | "user-group"
  | "partner"
  | "group"
  | "product";

export interface EffectivePrice {
  value: number;
  source: PriceSource;
}

/**
 * Source-aware resolver. Same hierarchy as the legacy
 * `useEffectivePriceLookup` (`user-partner > user-group > company-partner
 * > company-group > product-default`) but returns `{ value, source }`
 * so the PriceCell can render a TARIF/LISTE pill alongside the phantom
 * preview. Returns `null` when no tier resolves a price for the
 * `(productId, partnerId)` pair.
 */
export function useEffectivePriceWithSourceLookup(priceMaps: PriceLookupMaps) {
  return useCallback(
    (
      productId: string | null,
      partnerId: string | null,
    ): EffectivePrice | null => {
      if (!productId) return null;

      // Defensive `?.get`: this resolver is invoked from recap totals
      // (computeFlowStats) which run during render, including in test
      // environments that may stub `priceMaps` as `{}` to skip the
      // global-prices subscription. The legacy `useEffectivePriceLookup`
      // is never invoked during render (it's only triggered by a
      // product pick), so it never tripped on partial maps — this one
      // does, and we choose tolerance over forcing every test to
      // construct a full `PriceLookupMaps` shape.
      if (partnerId) {
        const userPartnerProductPrices =
          priceMaps.userPartnerPrices?.get(partnerId);
        if (userPartnerProductPrices?.has(productId)) {
          return {
            value: userPartnerProductPrices.get(productId)!,
            source: "user-partner",
          };
        }

        const groupId = priceMaps.partnerGroups?.get(partnerId);
        if (groupId) {
          const userGroupProductPrices =
            priceMaps.userGroupPrices?.get(groupId);
          if (userGroupProductPrices?.has(productId)) {
            return {
              value: userGroupProductPrices.get(productId)!,
              source: "user-group",
            };
          }
        }

        const partnerProductPrices = priceMaps.partnerPrices?.get(partnerId);
        if (partnerProductPrices?.has(productId)) {
          return {
            value: partnerProductPrices.get(productId)!,
            source: "partner",
          };
        }

        if (groupId) {
          const groupProductPrices = priceMaps.groupPrices?.get(groupId);
          if (groupProductPrices?.has(productId)) {
            return {
              value: groupProductPrices.get(productId)!,
              source: "group",
            };
          }
        }
      }

      if (priceMaps.productPrices?.has(productId)) {
        return {
          value: priceMaps.productPrices.get(productId)!,
          source: "product",
        };
      }

      return null;
    },
    [priceMaps],
  );
}

/**
 * Hook to get the effective price for a product-partner combination.
 * Uses the pricing hierarchy:
 * user partner > user group > company partner > company group > product default.
 *
 * Partner-level slots are empty under the V2 Dexie schema (see
 * `useGlobalPrices` notes); the resolver falls through to client-group
 * prices.
 */
export function useEffectivePriceLookup(priceMaps: PriceLookupMaps) {
  const lookupWithSource = useEffectivePriceWithSourceLookup(priceMaps);
  return useCallback(
    (productId: string | null, partnerId: string | null): number | null => {
      const result = lookupWithSource(productId, partnerId);
      return result ? result.value : null;
    },
    [lookupWithSource],
  );
}

/**
 * Collapses the 5-tier `PriceSource` into the 2-bucket UI tag used by
 * the recap price preview pill. Anything sourced from a negotiated
 * tier (user/company × partner/group) is "negotiated" → TARIF. The
 * product default is "list" → LISTE. Lets the cell render one of two
 * static labels without re-deriving the bucket per row.
 */
export type PriceSourceBucket = "negotiated" | "list";

export function priceSourceBucket(source: PriceSource): PriceSourceBucket {
  return source === "product" ? "list" : "negotiated";
}

// `useManualRefreshPrices` + `prefetchPricingCache` removed as part of the
// TanStack → Dexie shim migration: the REST endpoints they invalidated /
// prefetched (`pricingKeys.companyPartnerPricesGlobal`, `api.pricing.getAll*`)
// no longer exist, and no caller in `frontend/src/**` consumed either.
// SyncEngine + the SSE `prices` channel keep the Dexie table fresh.
